/**
 * PolicyRuleForm — Add/Edit rule modal
 *
 * Full form with layer, channel, purpose, B2B/B2C, required checks
 * (multi-select with inline param editors), effect, remediation,
 * source reference, priority, and overridable toggle.
 */
import { useState, useEffect, useMemo } from 'react';
import { Button, Input, Label } from '@respectly/ui';
import { Switch } from '@respectly/ui';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@respectly/ui';
import { Modal } from '@respectly/ui';
import { Loader2, Plus, X, ShieldCheck, Trash2 } from 'lucide-react';
import type {
  Rule,
  RuleLayer,
  RuleEffect,
  PREChannel,
  MessagePurpose,
  PRECheckDefinition,
} from '@respectly/shared-types';
import { PRE_CHECK_DEFINITIONS } from '../../data/pre-rules';
import { usePolicyStore } from '../../stores/usePolicyStore';
import { PolicyRuleParamEditor } from './PolicyRuleParamEditor';

// ============================================
// Types
// ============================================

interface PolicyRuleFormProps {
  isOpen: boolean;
  onClose: () => void;
  rule?: Rule | null;
  jurisdictionCode: string;
  defaultChannel?: PREChannel;
  defaultPurpose?: MessagePurpose;
  defaultLayer?: RuleLayer;
}

interface CheckEntry {
  checkId: string;
  required: boolean;
  params: Record<string, unknown> | null;
}

interface FormState {
  ruleId: string;
  name: string;
  description: string;
  layer: RuleLayer;
  channel: PREChannel | '';
  messagePurpose: MessagePurpose | '';
  b2bOrB2c: 'B2B' | 'B2C' | '';
  requiredChecks: CheckEntry[];
  effect: RuleEffect | '';
  remediation: string;
  sourceRef: string;
  userFacingSummary: string;
  userFacingCitation: string;
  priority: number;
  isOverridable: boolean;
}

// ============================================
// Helpers
// ============================================

function generateCustomId(jurisdictionCode: string): string {
  const num = String(Math.floor(Math.random() * 999) + 1).padStart(3, '0');
  return `CUSTOM-${jurisdictionCode}-${num}`;
}

function titleCase(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * Derive a deterministic, human-readable name for consent-capture rules so the
 * displayed name never drifts from the actual effect/channel/purpose. Returns
 * something like "US-FED — BLOCK Marketing SMS".
 */
export function deriveConsentCaptureRuleName(parts: {
  jurisdictionCode: string;
  effect: RuleEffect | '' | null | undefined;
  messagePurpose: MessagePurpose | '' | null | undefined;
  channel: PREChannel | '' | null | undefined;
}): string {
  const j = parts.jurisdictionCode || 'GLOBAL';
  const e = parts.effect || 'EFFECT';
  const p = parts.messagePurpose ? titleCase(parts.messagePurpose) : 'Any';
  const c = parts.channel || 'ALL';
  return `${j} — ${e} ${p} ${c}`;
}

const EMPTY_FORM = (
  jurisdictionCode: string,
  defaultChannel: PREChannel | '' = '',
  defaultPurpose: MessagePurpose | '' = '',
  defaultLayer: RuleLayer = 'constraint',
): FormState => ({
  ruleId: generateCustomId(jurisdictionCode),
  name: '',
  description: '',
  layer: defaultLayer,
  channel: defaultChannel,
  messagePurpose: defaultPurpose,
  b2bOrB2c: '',
  requiredChecks: [],
  effect: '',
  remediation: '',
  sourceRef: '',
  userFacingSummary: '',
  userFacingCitation: '',
  priority: 100,
  isOverridable: false,
});

const CHANNEL_OPTIONS = [
  { value: '', label: 'All Channels' },
  { value: 'SMS', label: 'SMS' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'VOICE', label: 'Voice' },
];

const PURPOSE_OPTIONS = [
  { value: '', label: 'All Purposes' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'INFORMATIONAL', label: 'Informational' },
];

const B2B_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'B2B', label: 'B2B' },
  { value: 'B2C', label: 'B2C' },
];

const LAYER_OPTIONS: { value: RuleLayer; label: string }[] = [
  { value: 'consent_capture', label: 'Consent Capture (Layer 0)' },
  { value: 'eligibility', label: 'Eligibility (Layer 1)' },
  { value: 'constraint', label: 'Operational Constraint (Layer 2)' },
];

const EFFECT_OPTIONS: { value: RuleEffect; label: string }[] = [
  { value: 'BLOCK', label: 'Block' },
  { value: 'CONDITION', label: 'Condition' },
  { value: 'ALLOW', label: 'Allow' },
];

// ============================================
// Component
// ============================================

export function PolicyRuleForm({
  isOpen,
  onClose,
  rule,
  jurisdictionCode,
  defaultChannel,
  defaultPurpose,
  defaultLayer,
}: PolicyRuleFormProps) {
  const isEdit = !!rule;
  const createRule = usePolicyStore((s) => s.createRule);
  const updateRule = usePolicyStore((s) => s.updateRule);
  const deleteRule = usePolicyStore((s) => s.deleteRule);
  const isCreating = usePolicyStore((s) => s.isCreating);
  const isUpdating = usePolicyStore((s) => s.isUpdating);
  const isDeleting = usePolicyStore((s) => s.isDeleting);
  const actionError = usePolicyStore((s) => s.actionError);
  const clearError = usePolicyStore((s) => s.clearError);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [form, setForm] = useState<FormState>(() =>
    EMPTY_FORM(jurisdictionCode, defaultChannel ?? '', defaultPurpose ?? '', defaultLayer),
  );
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  // Build check definition map
  const checkDefMap = useMemo(
    () => new Map<string, PRECheckDefinition>(PRE_CHECK_DEFINITIONS.map((c) => [c.checkId, c])),
    []
  );

  // Available checks (not yet selected)
  const availableChecks = useMemo(
    () =>
      PRE_CHECK_DEFINITIONS.filter(
        (c) => !form.requiredChecks.some((rc) => rc.checkId === c.checkId)
      ),
    [form.requiredChecks]
  );

  useEffect(() => {
    if (rule) {
      setForm({
        ruleId: rule.ruleId,
        name: rule.name,
        description: rule.description,
        layer: rule.layer,
        channel: (rule.channel as PREChannel) ?? '',
        messagePurpose: (rule.messagePurpose as MessagePurpose) ?? '',
        b2bOrB2c: (rule.b2bOrB2c as 'B2B' | 'B2C') ?? '',
        requiredChecks: rule.requiredChecks.map((c) => ({
          checkId: c.checkId,
          required: c.required,
          params: c.params ? { ...c.params } : null,
        })),
        effect: rule.effect as RuleEffect,
        remediation: rule.remediation ?? '',
        sourceRef: rule.sourceRef ?? '',
        userFacingSummary: rule.userFacingSummary ?? '',
        userFacingCitation: rule.userFacingCitation ?? '',
        priority: rule.priority,
        isOverridable: rule.isOverridable,
      });
    } else {
      setForm(
        EMPTY_FORM(
          jurisdictionCode,
          defaultChannel ?? '',
          defaultPurpose ?? '',
          defaultLayer,
        ),
      );
    }
    setErrors({});
    clearError();
  }, [rule, isOpen, jurisdictionCode, defaultChannel, defaultPurpose, defaultLayer, clearError]);

  const handleClose = () => {
    clearError();
    onClose();
  };

  const handleChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => {
      const next: FormState = { ...prev, [key]: value };
      // When switching INTO consent_capture, clear locked fields so we don't
      // accidentally POST stale Required-Checks / B2B values. Purpose is
      // retained because consent_capture rules can target either purpose.
      if (key === 'layer' && value === 'consent_capture') {
        next.requiredChecks = [];
        next.b2bOrB2c = '';
        if (next.effect && next.effect !== 'ALLOW' && next.effect !== 'BLOCK') {
          next.effect = '';
        }
      }
      return next;
    });
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const addCheck = (checkId: string) => {
    const def = checkDefMap.get(checkId);
    if (!def) return;
    setForm((prev) => ({
      ...prev,
      requiredChecks: [
        ...prev.requiredChecks,
        { checkId, required: true, params: def.paramSchema ? {} : null },
      ],
    }));
  };

  const removeCheck = (checkId: string) => {
    setForm((prev) => ({
      ...prev,
      requiredChecks: prev.requiredChecks.filter((c) => c.checkId !== checkId),
    }));
  };

  const updateCheckParams = (checkId: string, params: Record<string, unknown>) => {
    setForm((prev) => ({
      ...prev,
      requiredChecks: prev.requiredChecks.map((c) =>
        c.checkId === checkId ? { ...c, params } : c
      ),
    }));
  };

  const isConsentCapture = form.layer === 'consent_capture';

  // For Layer-0 rules the name is derived from the rule's effect/channel/purpose/
  // jurisdiction so it can never drift from the actual behavior. For other layers
  // the user-typed name is preserved.
  const derivedConsentCaptureName = useMemo(
    () =>
      deriveConsentCaptureRuleName({
        jurisdictionCode,
        effect: form.effect,
        messagePurpose: form.messagePurpose,
        channel: form.channel,
      }),
    [jurisdictionCode, form.effect, form.messagePurpose, form.channel],
  );
  const displayName = isConsentCapture ? derivedConsentCaptureName : form.name;

  const validate = (): boolean => {
    const newErrors: Partial<Record<string, string>> = {};
    if (!isConsentCapture && !form.name.trim()) newErrors.name = 'Name is required';
    if (!form.effect) newErrors.effect = 'Effect is required';
    if (isConsentCapture) {
      if (form.effect && form.effect !== 'ALLOW' && form.effect !== 'BLOCK') {
        newErrors.effect = 'Consent-capture rules must be ALLOW or BLOCK';
      }
    } else if (form.requiredChecks.length === 0) {
      newErrors.requiredChecks = 'At least one check is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const dto = {
      ruleId: form.ruleId,
      // For Layer-0 rules, the name is derived from effect/channel/purpose/
      // jurisdiction — the user-typed value is ignored to prevent drift.
      name: isConsentCapture ? derivedConsentCaptureName : form.name.trim(),
      description: form.description.trim(),
      jurisdictionCode,
      layer: form.layer,
      channel: form.channel || null,
      // Consent-capture rules support either purpose (Informational and
      // Marketing); B2B/B2C is not relevant.
      messagePurpose: form.messagePurpose || null,
      b2bOrB2c: isConsentCapture ? null : form.b2bOrB2c || null,
      requiredChecks: isConsentCapture
        ? []
        : form.requiredChecks.map((c) => ({
            checkId: c.checkId,
            required: c.required,
            params: c.params && Object.keys(c.params).length > 0 ? c.params : null,
          })),
      effect: form.effect as RuleEffect,
      remediation: form.remediation.trim() || null,
      sourceRef: form.sourceRef.trim() || null,
      userFacingSummary: form.userFacingSummary.trim() || null,
      userFacingCitation: form.userFacingCitation.trim() || null,
      priority: form.priority,
      isActive: true,
      isOverridable: isConsentCapture ? false : form.isOverridable,
      isSystem: false,
    };

    if (isEdit && rule) {
      const success = await updateRule(rule.id, dto);
      if (success) handleClose();
    } else {
      const id = await createRule(dto);
      if (id) handleClose();
    }
  };

  const isSaving = isCreating || isUpdating;
  // Inherited rules (rule.jurisdictionCode ≠ the page's selected jurisdiction)
  // are read-only here — author them at their owning jurisdiction.
  const isInheritedFromAncestor = isEdit && !!rule && rule.jurisdictionCode !== jurisdictionCode;

  const handleDelete = async () => {
    if (!isEdit || !rule) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    const ok = await deleteRule(rule.id);
    if (ok) {
      setConfirmDelete(false);
      handleClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="large"
      title={isEdit ? `Edit Rule: ${rule?.ruleId}` : 'Add Rule'}
    >
      <form onSubmit={handleSubmit} className="space-y-4 pt-2 max-h-[70vh] overflow-y-auto pr-1">
        {/* Rule ID (read-only for edit) */}
        <div className="space-y-1.5">
          <Label className="text-sm">Rule ID</Label>
          <Input value={form.ruleId} disabled className="font-mono text-sm" />
        </div>

        {/* Name + Description */}
        <div className="space-y-1.5">
          <Label className="text-sm">Name {isConsentCapture ? '' : '*'}</Label>
          {isConsentCapture ? (
            <>
              <Input value={displayName} disabled className="font-mono text-sm" />
              <p className="text-[11px] text-muted-foreground">
                Auto-generated from jurisdiction, effect, purpose and channel so the name can never
                contradict the rule. Use Description for free-text notes.
              </p>
            </>
          ) : (
            <Input
              placeholder="Human-readable rule name"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={errors.name}
            />
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Description</Label>
          <Input
            placeholder="What this rule enforces"
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
          />
        </div>

        {/* Layer + Effect */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm">Layer</Label>
            <Select
              value={form.layer}
              onValueChange={(v) => handleChange('layer', v as RuleLayer)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LAYER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Effect *</Label>
            <Select
              value={form.effect || '_empty_'}
              onValueChange={(v) =>
                handleChange('effect', (v === '_empty_' ? '' : v) as RuleEffect | '')
              }
            >
              <SelectTrigger error={!!errors.effect}>
                <SelectValue placeholder="Select effect..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_empty_" disabled>
                  Select effect...
                </SelectItem>
                {EFFECT_OPTIONS.filter((o) =>
                  isConsentCapture ? o.value === 'ALLOW' || o.value === 'BLOCK' : true
                ).map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.effect && (
              <p className="text-xs text-red-500">{errors.effect}</p>
            )}
          </div>
        </div>

        {/* Channel + Purpose + B2B/B2C */}
        <div className={`grid gap-3 ${isConsentCapture ? 'grid-cols-2' : 'grid-cols-3'}`}>
          <div className="space-y-1.5">
            <Label className="text-sm">Channel</Label>
            <Select
              value={form.channel || '_all_'}
              onValueChange={(v) =>
                handleChange('channel', (v === '_all_' ? '' : v) as PREChannel | '')
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHANNEL_OPTIONS.map((o) => (
                  <SelectItem key={o.value || '_all_'} value={o.value || '_all_'}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Purpose</Label>
            <Select
              value={form.messagePurpose || '_all_'}
              onValueChange={(v) =>
                handleChange(
                  'messagePurpose',
                  (v === '_all_' ? '' : v) as MessagePurpose | ''
                )
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PURPOSE_OPTIONS.map((o) => (
                  <SelectItem key={o.value || '_all_'} value={o.value || '_all_'}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!isConsentCapture && (
            <>
              <div className="space-y-1.5">
                <Label className="text-sm">B2B/B2C</Label>
                <Select
                  value={form.b2bOrB2c || '_all_'}
                  onValueChange={(v) =>
                    handleChange('b2bOrB2c', (v === '_all_' ? '' : v) as 'B2B' | 'B2C' | '')
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {B2B_OPTIONS.map((o) => (
                      <SelectItem key={o.value || '_all_'} value={o.value || '_all_'}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        {isConsentCapture && (
          <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-900/10 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-300">
            <strong>Layer 0 — Consent Capture.</strong> Applies only to initial
            INFORMATIONAL consent at contact creation. Marketing is never
            auto-granted. Required Checks, Purpose, and B2B/B2C are not used.
          </div>
        )}

        {/* Required Checks (hidden for consent_capture rules) */}
        {!isConsentCapture && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" />
              Required Checks *
            </Label>
            {availableChecks.length > 0 && (
              <Select
                value="_add_"
                onValueChange={(v) => {
                  if (v !== '_add_') addCheck(v);
                }}
              >
                <SelectTrigger className="w-48 h-7 text-xs">
                  <div className="flex items-center gap-1">
                    <Plus className="w-3 h-3" />
                    <span>Add check...</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_add_" disabled>
                    Select a check...
                  </SelectItem>
                  {availableChecks.map((c) => (
                    <SelectItem key={c.checkId} value={c.checkId}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {errors.requiredChecks && (
            <p className="text-xs text-red-500">{errors.requiredChecks}</p>
          )}

          {form.requiredChecks.length === 0 && (
            <p className="text-xs text-slate-400 dark:text-slate-500 py-2">
              No checks selected. Use the dropdown above to add checks.
            </p>
          )}

          <div className="space-y-2">
            {form.requiredChecks.map((entry) => {
              const def = checkDefMap.get(entry.checkId);
              return (
                <div
                  key={entry.checkId}
                  className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {def?.name ?? entry.checkId}
                      </span>
                      {def?.description && (
                        <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                          {def.description}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCheck(entry.checkId)}
                      className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {def?.paramSchema && (
                    <PolicyRuleParamEditor
                      checkId={entry.checkId}
                      paramSchema={def.paramSchema as Record<string, string>}
                      params={entry.params}
                      onChange={(params) => updateCheckParams(entry.checkId, params)}
                      className="ml-2 pl-2 border-l-2 border-slate-200 dark:border-slate-700"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* Remediation (not used for consent_capture) */}
        {!isConsentCapture && (
        <div className="space-y-1.5">
          <Label className="text-sm">Remediation</Label>
          <Input
            placeholder="What would make a blocked send allowed?"
            value={form.remediation}
            onChange={(e) => handleChange('remediation', e.target.value)}
          />
        </div>
        )}

        {/* Source Ref + Priority */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm">Source Reference</Label>
            <Input
              placeholder="Legal citation (optional)"
              value={form.sourceRef}
              onChange={(e) => handleChange('sourceRef', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Priority</Label>
            <Input
              type="number"
              min={0}
              value={String(form.priority)}
              onChange={(e) =>
                handleChange('priority', parseInt(e.target.value) || 0)
              }
            />
          </div>
        </div>

        {/* Tenant-facing wording (optional overrides shown on Policies page) */}
        <div className="space-y-1.5 pt-2 border-t border-slate-200 dark:border-slate-700">
          <Label className="text-sm">Tenant-facing summary (optional)</Label>
          <p className="text-xs text-slate-400 dark:text-slate-500">
            When set, replaces all per-check bullets for this rule on the tenant
            Policies page with a single sentence. Leave blank to render each
            required check via its own user-facing template.
          </p>
          <Input
            placeholder="e.g. Recipient must have written express consent before any wireless marketing send."
            value={form.userFacingSummary}
            onChange={(e) => handleChange('userFacingSummary', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Tenant-facing citation (optional)</Label>
          <p className="text-xs text-slate-400 dark:text-slate-500">
            Short citation shown under the rule's bullets. Falls back to Source
            Reference when blank.
          </p>
          <Input
            placeholder="e.g. TCPA §227(b)(1)(A)"
            value={form.userFacingCitation}
            onChange={(e) => handleChange('userFacingCitation', e.target.value)}
          />
        </div>

        {/* Overridable (consent_capture rules are platform-managed, never overridable) */}
        {!isConsentCapture && (
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm">Overridable by Tenants</Label>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Allow tenants to adjust parameters for this rule
            </p>
          </div>
          <Switch
            checked={form.isOverridable}
            onCheckedChange={(checked: boolean) =>
              handleChange('isOverridable', checked)
            }
          />
        </div>
        )}

        {/* Server error (e.g. failed save) */}
        {actionError && (
          <div className="rounded-md border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-700 dark:text-red-300">
            {actionError.message}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-200 dark:border-slate-700">
          {isEdit && !isInheritedFromAncestor ? (
            <Button
              type="button"
              variant={confirmDelete ? 'destructive' : 'outline'}
              onClick={handleDelete}
              disabled={isSaving || isDeleting}
              className={confirmDelete ? '' : 'text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 border-red-200 dark:border-red-900/60'}
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              {confirmDelete ? 'Click again to confirm delete' : 'Delete rule'}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSaving || isDeleting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || isDeleting || isInheritedFromAncestor}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Rule'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
